import { useEffect, useRef, useState } from "react";
import { Search, Loader2, MapPin } from "lucide-react";

// Appels directs navigateur, chacun avec timeout 2.5s, rendu PROGRESSIF :
// la France (geo.api.gouv.fr) et la Belgique (photon.komoot.io) sont indépendantes ;
// si l'une est lente/injoignable, l'autre s'affiche quand même.
function withTimeout(url, ms = 2500) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { signal: ctrl.signal }).then((r) => r.json()).finally(() => clearTimeout(t));
}

async function fetchFR(q) {
  const url = `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(q)}&fields=nom,codeDepartement,centre&boost=population&limit=8`;
  const data = await withTimeout(url);
  if (!Array.isArray(data)) return [];
  return data
    .filter((c) => c.centre?.coordinates)
    .map((c) => ({ key: `fr-${c.nom}-${c.codeDepartement}`, name: c.nom, badge: c.codeDepartement || "FR",
      label: `${c.nom} (${c.codeDepartement || "FR"})`, lat: c.centre.coordinates[1], lng: c.centre.coordinates[0] }));
}

async function fetchBE(q) {
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&lang=fr&limit=8&layer=city&layer=locality`;
  const data = await withTimeout(url);
  const out = [];
  const seen = new Set();
  for (const f of data?.features || []) {
    const p = f.properties || {};
    if (p.countrycode !== "BE" || !p.name || seen.has(p.name)) continue;
    seen.add(p.name);
    const coords = f.geometry?.coordinates;
    if (!coords) continue;
    out.push({ key: `be-${p.name}`, name: p.name, badge: "BE", label: `${p.name} (BE)`, lat: coords[1], lng: coords[0] });
  }
  return out;
}

function mergeDedup(prev, incoming) {
  const map = new Map(prev.map((x) => [x.key, x]));
  for (const it of incoming) if (!map.has(it.key)) map.set(it.key, it);
  const arr = [...map.values()];
  arr.sort((a, b) => (a.badge === "BE" ? 1 : 0) - (b.badge === "BE" ? 1 : 0));
  return arr.slice(0, 10);
}

export default function CityAutocomplete({ value, onChange, onSelect, placeholder, testid = "city", inputClassName }) {
  const [sugg, setSugg] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("touchstart", onDoc); };
  }, []);

  const handleChange = (text) => {
    onChange(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    reqIdRef.current += 1;
    const myId = reqIdRef.current;
    const q = text.trim();
    if (q.length < 2) { setSugg([]); setOpen(false); setLoading(false); return; }
    setLoading(true);
    setOpen(true);
    setSugg([]);
    timerRef.current = setTimeout(() => {
      // France : fournisseur principal, pilote l'indicateur de chargement
      fetchFR(q)
        .then((res) => { if (reqIdRef.current === myId) setSugg((prev) => mergeDedup(prev, res)); })
        .catch(() => {})
        .finally(() => { if (reqIdRef.current === myId) setLoading(false); });
      // Belgique : indépendante, fusionnée dès qu'elle répond
      fetchBE(q)
        .then((res) => { if (reqIdRef.current === myId && res.length) setSugg((prev) => mergeDedup(prev, res)); })
        .catch(() => {});
    }, 250);
  };

  const pick = (item) => {
    onChange(item.name);
    setOpen(false);
    setSugg([]);
    onSelect?.(item);
  };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-0">
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none z-10" />
      <input data-testid={`${testid}-input`} value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => { if (sugg.length) setOpen(true); }}
        autoComplete="off" placeholder={placeholder}
        className={inputClassName || "w-full h-9 pl-8 pr-2 rounded-lg bg-[#0B0F17] border border-slate-700 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"} />
      {loading && <Loader2 size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      {open && (sugg.length > 0 || (!loading && value.trim().length >= 2)) && (
        <ul data-testid={`${testid}-suggestions`}
          className="absolute left-0 right-0 top-full mt-1 z-[1200] bg-[#0B0F17] border border-slate-600 rounded-xl shadow-2xl overflow-hidden max-h-72 overflow-y-auto">
          {sugg.length === 0 && !loading && (
            <li className="px-3 py-3 text-sm text-slate-400">Aucune ville trouvée</li>
          )}
          {sugg.map((s) => (
            <li key={s.key}>
              <button type="button" data-testid={`${testid}-option-${s.name}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
                className="w-full h-11 px-3 flex items-center gap-2 text-left text-sm text-slate-100 hover:bg-slate-800 active:bg-slate-700">
                <MapPin size={15} className="shrink-0 text-sky-400" />
                <span className="flex-1 truncate">{s.name}</span>
                <span className="shrink-0 text-xs font-bold text-slate-400 bg-slate-800 rounded px-1.5 py-0.5">{s.badge}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
